<template>
  <div class="overflow-hidden rounded-md border border-slate-400">
    <table class="w-full table-fixed border-collapse">
      <thead class="bg-slate-200">
        <tr class="text-left">
          <th class="min-w-1/5 p-2">User</th>
          <th class="p-2">Role</th>
          <th class="w-24 p-2">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="account in accounts" :key="account.id" class="border-t border-slate-400">
          <td class="min-w-1/5 p-2">
            <div class="flex">
              <UserCircleIcon class="h-6 w-6 mr-2 shrink-0" />
              <span>{{ account.email }}</span>
            </div>
          </td>
          <td class="p-2">
            <div v-if="canEditRole(account)" class="flex items-center">
              <div class="relative">
                <select
                  :value="account.role"
                  @change="handleRoleChange(account, ($event.target as HTMLSelectElement).value)"
                  :disabled="isUpdatingRole === account.id"
                  class="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[80px]"
                  style="
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    appearance: none;
                    background-image: none !important;
                    background: white !important;
                    padding-right: 2rem !important;
                  "
                >
                  <option v-for="role in getAvailableRoles()" :key="role" :value="role">
                    {{ role }}
                  </option>
                </select>
                <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
              <div v-if="isUpdatingRole === account.id" class="ml-2">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              </div>
            </div>
            <span v-else>{{ account.role }}</span>
          </td>
          <td class="p-2">
            <button
              @click="handleCancelInvitation(account)"
              class="text-red-600 hover:text-red-800"
              title="Cancel invitation"
              v-if="account.invitation_accepted_at === null && account.invitation_sent_at !== null"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { UserCircleIcon, XMarkIcon } from '@heroicons/vue/24/solid'
import { type WorkspaceAccount } from '@/questionnaire/types'
import { updateUserRole } from '@/services/settingsService'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { errorNotification, successNotification } from '@/utils/notifications'

const props = defineProps<{
  accounts: WorkspaceAccount[]
  currentUserRole?: string
  onCancelInvitation?: (_account: WorkspaceAccount) => void
}>()

const queryClient = useQueryClient()
const isUpdatingRole = ref<string | null>(null)

const updateRoleMutation = useMutation({
  mutationFn: ({ accountWorkspaceId, role }: { accountWorkspaceId: string; role: string }) => updateUserRole(accountWorkspaceId, role),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['settings'] })
    successNotification('Role updated successfully')
  },
  onError: (error: Error & { response?: { data?: { error?: string } } }) => {
    const message = error.response?.data?.error || 'Failed to update role'
    errorNotification(message)
  },
  onSettled: () => {
    isUpdatingRole.value = null
  },
})

const handleCancelInvitation = (account: WorkspaceAccount) => {
  props.onCancelInvitation?.(account)
}

const canEditRole = (account: WorkspaceAccount): boolean => {
  // Only show role editing for accepted users (not pending invitations)
  if (!account.invitation_accepted_at) return false

  // Current user must be admin or owner to edit roles
  if (!props.currentUserRole || !['admin', 'owner'].includes(props.currentUserRole)) return false

  return true
}

const getAvailableRoles = (): string[] => {
  const hasOwner = props.accounts.some(account => account.role === 'owner')

  if (props.currentUserRole === 'owner') {
    // Only show owner option if there's no existing owner
    const roles = ['user', 'admin']
    if (!hasOwner) {
      roles.push('owner')
    }
    return roles
  } else if (props.currentUserRole === 'admin') {
    return ['user', 'admin']
  }
  return []
}

const handleRoleChange = async (account: WorkspaceAccount, newRole: string) => {
  if (newRole === account.role) return

  isUpdatingRole.value = account.id
  await updateRoleMutation.mutateAsync({
    accountWorkspaceId: account.account_workspace_id,
    role: newRole,
  })
}
</script>
